import React, { useContext, useMemo } from 'react';

import { Descendant } from 'slate';

// components
import CopyToClipboardBox from '../../components/shared/CopyToClipboardBox';
import styled from 'styled-components';

// constants
import aampeTheme from '../../constants/aampeTheme';
import { FormulaType } from '../../constants/formulaType';

// context
import { EditorContext } from '../../context/EditorContext';

// hooks
import useContentApiBaseUrl from '../../hooks/helper/useContentApiBaseUrl';

import {
    ElementKind,
    ImageElement,
    SectionElement,
} from '../../models/editor.model';
// models
import { ContentCardType, FormulaTypesData } from '../../models/formula.model';

const FormulaAampiConnect: React.FC<React.PropsWithChildren<unknown>> = () => {
    const {
        editorDetails: { formula, formulaType },
    } = useContext(EditorContext);
    const contentApiBaseUrl = useContentApiBaseUrl();

    const contentAPI = useMemo<string>(() => {
        if (formula?.id === undefined) {
            return 'Please complete the Setup page in order to generate the Aampe endpoint code snippet.';
        } else {
            return `{% connected_content ${contentApiBaseUrl}/in-app?contact_id={{${'$'}{user_id}}}&campaign_id=${
                formula?.id
            } :method get :headers { "X-API-KEY": "YOUR-TOKEN" } :content_type application/json :save aampe_content :retry %}\n\n{% if aampe_content == null %}\n {% abort_message('Aampe Content API returned an error') %}\n{% endif %}`;
        }
    }, [formula, contentApiBaseUrl]);

    const structure = useMemo<Descendant[] | undefined>(() => {
        return FormulaTypesData[formulaType as FormulaType]?.getTemplate({
            content_card_type:
                formula?.content_card_type || ContentCardType.TextOnly,
        });
    }, [formula, formulaType]);

    const aampiJson = useMemo<string | undefined>(
        () =>
            structure
                ?.map((section) => {
                    if ((section as ImageElement)?.kind === ElementKind.image) {
                        return `{{aampe_content.${
                            (section as ImageElement)?.kind
                        }_url}}\n`;
                    }
                    return `{{aampe_content.${
                        (section as SectionElement)?.sectionType
                    }}}\n`;
                })
                .join(''),
        [structure],
    );

    return (
        <Container>
            <Title>Aampe endpoint</Title>
            <BodyContainer>
                <CopyToClipboardBox copyData={contentAPI} />
                <CopyToClipboardBox copyData={aampiJson ?? ''} />
            </BodyContainer>
        </Container>
    );
};

export default FormulaAampiConnect;

const Container = styled.div({
    display: 'flex',
    width: '100%',
    alignItems: 'start',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(4),
});

const BodyContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(5),
    width: '50%',
});

const Title = styled.span({
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '20px',
});
